// The scheduling of rules and blocks, as the Scheduling diagram reads and
// changes it. Pure (no React, no MemoryDB): the diagram applies the change to
// the values of the open form or to a row of the draft.
//
// A rule keeps its statements in `rule`, a block its sub-blocks in `blocks`.
// A statement is scheduled by a top-level condition of its conditionRaw whose
// second value is the range "start÷end"; the statement keeps the ranges in
// `scheduling` ([{ id, start, end }], id: the id of the condition), and the
// row keeps all of them in `scheduling` and, per statement index, in
// `schedulingMap` ([[index, scheduling]]): a summary of its statements
// (scheduleOf), written again when they change (withSchedule).

import cloneDeep from "lodash.clonedeep";
import isEqual from "lodash.isequal";
import moment from "moment";
import { complexToBasic } from "safe-evaluate-expression";

const RANGE_SEPARATOR = "÷";

const strip = (value) => String(value).replace(/"/g, "");

// The statements of a rule (`rule`) or the sub-blocks of a block (`blocks`).
export const statementsKey = (row) =>
  Array.isArray(row?.rule)
    ? "rule"
    : Array.isArray(row?.blocks)
      ? "blocks"
      : null;

// The scheduling of a row, from the `scheduled` and `scheduling` of each of
// its statements: the ranges of the scheduled ones (`scheduling`), also by
// statement index (`schedulingMap`, in index order).
export function scheduleOf(statements) {
  const schedulingMap = [];
  (statements ?? []).forEach((statement, index) => {
    if (statement?.scheduled)
      schedulingMap.push([index, statement.scheduling ?? []]);
  });

  return {
    scheduled: schedulingMap.length > 0,
    scheduling: schedulingMap.flatMap(([, scheduling]) => scheduling),
    schedulingMap
  };
}

// The row with the scheduling of its statements (scheduleOf): the row itself
// when it already has it, so that a row without scheduling gets no new fields
// (a missing field is no scheduling; `scheduled` may be stored as 1/0).
export function withSchedule(row) {
  const key = statementsKey(row);
  if (!key) return row;

  const next = scheduleOf(row[key]);
  if (
    Boolean(row.scheduled) === next.scheduled &&
    isEqual(row.scheduling ?? [], next.scheduling) &&
    isEqual(row.schedulingMap ?? [], next.schedulingMap)
  )
    return row;

  return { ...row, ...next };
}

// The scheduling conditions are top-level ones (see getScheduling in
// db/refManager), as findCondition of the conditions box looks them up.
const findCondition = (id, data) => {
  if (!data) return undefined;
  if (data.and || data.or) return findCondition(id, data.and || data.or);
  return Array.isArray(data) ? data.find((d) => d?.id === id) : undefined;
};

// Groups (one per row) and items (one per range) of the timeline.
export function toTimeline(rows, itemType) {
  const groups = [];
  const items = [];

  for (const row of rows) {
    if (!row) continue;
    groups.push({ id: row.id, title: row.title, type: itemType });

    const statements = row[statementsKey(row)] ?? [];
    statements.forEach((statement, index) => {
      for (const schedule of statement?.scheduling ?? []) {
        items.push({
          title: statement.field || statement.title,
          id: schedule.id,
          subId: statement._id,
          group: row.id,
          type: itemType,
          colorGroup: index,
          start_time: moment(schedule.start),
          end_time: moment(schedule.end)
        });
      }
    });
  }

  return { groups, items };
}

// The row with the range `scheduleId` of the statement `subId` moved (edge
// "move", by its start) or resized (edge "left": the start, "right": the
// end) to `time`, with the scheduling of the row from its statements
// (withSchedule). Returns a new row that shares what did not change (the
// other statements, the other fields), or the row itself when there is
// nothing to change (the statement or the range are gone).
export function applyScheduleChange(row, { scheduleId, subId, time, edge }) {
  const key = statementsKey(row);
  if (!key) return row;

  const index = row[key].findIndex((statement) => statement?._id === subId);
  if (index === -1) return row;

  const statement = row[key][index];
  const conditionRaw = cloneDeep(statement.conditionRaw);
  const condition = findCondition(scheduleId, conditionRaw);
  const range = condition?.values?.[1];
  if (!range) return row;

  let [start, end] = strip(range.value).split(RANGE_SEPARATOR);
  const date = new Date(time).toISOString();

  if (edge === "move") {
    const diff = moment(date).diff(start);
    start = date;
    end = moment(end).add(diff).toISOString();
  } else if (edge === "left") start = date;
  else end = date;

  range.value = `"${start}${RANGE_SEPARATOR}${end}"`;

  const withDates = (schedule) =>
    schedule?.id === scheduleId ? { ...schedule, start, end } : schedule;
  const statements = [...row[key]];
  statements[index] = {
    ...statement,
    conditionRaw,
    condition: complexToBasic(conditionRaw),
    scheduling: (statement.scheduling ?? []).map(withDates)
  };

  // Not the stored scheduling of the row with the new dates: it may be stale
  // (ranges of deleted statements, old indexes), and a range of another
  // statement with the same condition id (a pasted condition) would move too.
  return withSchedule({ ...row, [key]: statements });
}
